import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { concatMap, map, Observable, of } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { ComparisonOperator, LogicalType, QueryNodeType } from '@blaze-shared/api-contracts-domain';
import { DatasetsApiService } from '@blaze-shared/datasets-data-access';
import { RuntimePage } from '@blaze-shared/elements-domain';
import { syncSelect } from '@blaze-shared/platform-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  BlazeTableSchema,
  DatasetRow,
  DatasetSchema,
  getIdColumnName,
} from '@blaze-shared/schema-domain';
import { isNullishOrEmptyString } from '@blaze-shared/shared-util';

interface PrepareNavigateToTableLinkData {
  dataRow: DatasetRow | undefined;
  dataRowSchema: BlazeTableSchema | DatasetSchema | undefined;
  page: RuntimePage | undefined;
}

@Injectable()
export class TableLinkCellActionService {
  private readonly datasetsApiService = inject(DatasetsApiService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);

  prepareNavigateToTableLink(
    targetPageId: string,
    dataRowId: string,
  ): Observable<PrepareNavigateToTableLinkData> {
    const page = syncSelect(this.store, runtimeSelectors.selectPageById(targetPageId));

    // If page could not be found - return undefined
    if (!page) {
      return of({
        page: undefined,
        dataRow: undefined,
        dataRowSchema: undefined,
      });
    }

    // Based on the page get the schema for the main dataset if provided
    const dataRowSchema$ = page?.mainDatasetId
      ? this.getSchemaForPageDataset(page.mainDatasetId)
      : of(undefined);

    return dataRowSchema$.pipe(
      concatMap((dataRowSchema) => {
        // If schema missing - data cannot be fetched
        if (!dataRowSchema) {
          return of({ dataRow: undefined, dataRowSchema: undefined });
        }

        // If schema present - try to fetch data row
        return this.tryFetchDataRow(dataRowSchema, dataRowId).pipe(
          map((dataRow) => ({ dataRow, dataRowSchema })),
        );
      }),
      // Gather all the data as return value
      map(({ dataRow, dataRowSchema }) => ({
        page,
        dataRow,
        dataRowSchema,
      })),
    );
  }

  private getSchemaForPageDataset(
    mainDatasetId: string,
  ): Observable<BlazeTableSchema | DatasetSchema | undefined> {
    return this.schemaService.obtainSchema<BlazeTableSchema | DatasetSchema>(mainDatasetId);
  }

  private tryFetchDataRow(
    dataRowSchema: BlazeTableSchema | DatasetSchema,
    dataRowId: string | undefined,
  ): Observable<DatasetRow | undefined> {
    const idColumn = getIdColumnName(dataRowSchema);
    if (!idColumn || isNullishOrEmptyString(dataRowId)) {
      return of(undefined);
    }

    return this.datasetsApiService
      .postGet({
        datasetId: dataRowSchema.id,
        filterQuery: {
          nodeType: QueryNodeType.Logical,
          logicalType: LogicalType.And,
          children: [
            {
              column: idColumn,
              displayInfos: [],
              isFormulaValue: false,
              isOptional: false,
              nodeType: QueryNodeType.Comparison,
              operator: ComparisonOperator.Eq,
              value: dataRowId,
            },
          ],
        },
      })
      .pipe(map((data) => data?.[0]));
  }
}
